import { useState, useEffect, useMemo } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './App.css'
import { VisualTweetFeed } from './components/VisualTweetFeed'
import { ViewSwitcher } from './components/ViewSwitcher'
import { Instructions } from './components/Instructions'
import { CombinedFilters } from './components/CombinedFilters'
import { Zborovi } from './components/Zborovi'
import { PdfViewer } from './components/PdfViewer'
import { ContactFooter } from './components/ContactFooter'
import { useAllTweets, TweetTableData } from './hooks/useAllTweets'
import { InstagramCarousel } from './components/InstagramCarousel'

const queryClient = new QueryClient()

type SortField = 'created_at' | 'like_count'
type SortOrder = 'asc' | 'desc'

function AppContent() {
  const [view, setView] = useState<'instructions' | 'monitor' | 'zborovi' | 'prava' | 'ig'>('instructions')
  
  // Filter states
  const [selectedHashtags, setSelectedHashtags] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [showRecentOnly, setShowRecentOnly] = useState(false);
  const [showTenMinOnly, setShowTenMinOnly] = useState(false);
  
  const tableTweets = useAllTweets()

  const handleHashtagChange = (hashtag: string) => {
    setSelectedHashtags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(hashtag)) {
        newSet.delete(hashtag);
      } else {
        newSet.add(hashtag);
      }
      return newSet;
    });
  };

  const handleSortChange = (field: SortField, order: SortOrder) => {
    setSortField(field);
    setSortOrder(order);
  };

  // Apply all filters and sorting with AND logic for hashtags
  const processedTweets = useMemo(() => {
    let filtered = tableTweets.data;
    
    // Apply hashtag filter with AND logic
    if (selectedHashtags.size > 0) {
      filtered = filtered.filter((tweet: TweetTableData) => {
        if (!tweet.hashtags || tweet.hashtags.length === 0) return false;
        
        // Check if tweet contains ALL selected hashtags (AND logic)
        const tweetHashtagsSet = new Set(tweet.hashtags);
        return Array.from(selectedHashtags).every(selectedTag => 
          tweetHashtagsSet.has(selectedTag)
        );
      });
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(tweet => 
        tweet.text.toLowerCase().includes(query) ||
        tweet.author_username.toLowerCase().includes(query) ||
        tweet.author_name.toLowerCase().includes(query)
      );
    }
    
    // Apply time filter - 10 min takes precedence over 30 min
    if (showTenMinOnly) {
      const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
      filtered = filtered.filter(tweet => 
        new Date(tweet.created_at) > tenMinutesAgo
      );
    } else if (showRecentOnly) {
      const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
      filtered = filtered.filter(tweet => 
        new Date(tweet.created_at) > thirtyMinutesAgo
      );
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      // Handle date sorting
      if (sortField === 'created_at') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
    
    return sorted;
  }, [tableTweets.data, selectedHashtags, searchQuery, showTenMinOnly, showRecentOnly, sortField, sortOrder]);

  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#monitor') setView('monitor')
      else if (window.location.hash === '#zborovi') setView('zborovi')
      else if (window.location.hash === '#prava') setView('prava')
      else if (window.location.hash === '#ig') setView('ig')
      else setView('instructions') // Default to instructions for no hash or any other hash
    }
    handleHashChange()
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  // Show Instagram carousel without header/footer for clean screenshots
  if (view === 'ig') {
    return <InstagramCarousel />
  }

  return (
    <div className="App h-screen flex flex-col bg-gray-50">
      <div className="flex justify-between items-center p-4 bg-white border-b shadow-sm">
        <h1 className="text-2xl font-bold text-gray-800">
          Širimo se
        </h1>
        <ViewSwitcher view={view} onViewChange={setView} />
      </div>

      <div className="relative flex-1 overflow-hidden flex flex-col">
        {view === 'instructions' ? (
          <div className="flex-1 overflow-auto">
            <Instructions />
            <ContactFooter />
          </div>
        ) : view === 'zborovi' ? (
          <div className="flex-1 overflow-auto">
            <Zborovi />
            <ContactFooter />
          </div>
        ) : view === 'prava' ? (
          <div className="flex-1 overflow-auto">
            <PdfViewer />
            <ContactFooter />
          </div>
        ) : (
          <div className="flex-1 bg-gray-50 flex flex-col overflow-hidden">
            {/* Desktop: Fixed filters at top */}
            <div className="hidden md:block p-4 flex-shrink-0">
              <CombinedFilters
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                sortField={sortField}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                showRecentOnly={showRecentOnly}
                onRecentOnlyChange={setShowRecentOnly}
                showTenMinOnly={showTenMinOnly}
                onTenMinOnlyChange={setShowTenMinOnly}
                selectedHashtags={selectedHashtags}
                onHashtagChange={handleHashtagChange}
                allTweets={tableTweets.data}
                filteredCount={processedTweets.length}
                totalCount={tableTweets.data.length}
              />
            </div>
            
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              {/* Mobile: Scrollable filters */}
              <div className="md:hidden p-3">
                <CombinedFilters
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSortChange={handleSortChange}
                  showRecentOnly={showRecentOnly}
                  onRecentOnlyChange={setShowRecentOnly}
                  showTenMinOnly={showTenMinOnly}
                  onTenMinOnlyChange={setShowTenMinOnly}
                  selectedHashtags={selectedHashtags}
                  onHashtagChange={handleHashtagChange}
                  allTweets={tableTweets.data}
                  filteredCount={processedTweets.length}
                  totalCount={tableTweets.data.length}
                />
              </div>
              
              <div className="pb-4">
                <VisualTweetFeed 
                  tweets={processedTweets} 
                  isLoading={tableTweets.isLoading}
                  totalCount={tableTweets.data.length}
                  searchQuery={searchQuery}
                  showRecentOnly={showRecentOnly}
                  showTenMinOnly={showTenMinOnly}
                />
                <ContactFooter />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  )
}

export default App 